import type { TakeoffColumnDefinition } from "./groundworksTakeoffColumnMap";

/**
 * Roads & Sewers take-off template column map.
 *
 * Generated from `public/R&S Take-off sheet.xlsx` — the same file
 * downloaded by "Download Excel Template" on /tender/import-takeoff. Unlike
 * the Groundworks template (whose `GWKS_*` key rows predate this feature),
 * this template had no unique-key row/column at all until one was added
 * per-sheet (`RS_<SHEET>_NNN`, e.g. `RS_FT_001`) specifically to support
 * this validation. Only sheets that have been keyed so far are included —
 * `roadsAndSewersTakeoffImport.ts` (and the helper modules it calls:
 * `foulSewersExcelUtils.ts`, `stormSewersExcelUtils.ts`, `posExcelUtils.ts`,
 * `compoundExcelUtils.ts`, `substationBaseExcelUtils.ts`, `roadsExcelUtils.ts`'s
 * `parseMainServicesTrenching`, and its own inline "Prelims" handling) also
 * reads several sheets that have no keys yet (`Foul Summary`, `Storm Summary`,
 * `Carriageway Summary`, `Capping Layers`, `Spoil Reconcilliation`) — those
 * are simply absent from this map until keyed, same "no entry = not
 * validated yet" convention Groundworks already used for its own
 * not-yet-labelled sheets.
 *
 * **2026-09-09 re-sync**: `public/R&S Take-off sheet.xlsx` (then named
 * `M&J - R&S Take off sheet.xlsx`, renamed 2026-09-22) was
 * regenerated and every mapped sheet's key positions shifted (rows
 * inserted/removed throughout `Prelims`, `Ponds & Swales`, etc. — see e.g.
 * `RS_P_0015`/`A26` and `RS_P_0038`/`A66`, both new rows not present in the
 * prior template revision). This map was re-derived from scratch by scanning
 * every cell of the new file for the `RS_<SHEET>_NNN` pattern (not hand-
 * edited) — do not assume the old `cellRef`s still hold. Six sheets that
 * previously had no keys at all now do and have been added: `Pump Station`,
 * `Cellular Attenuation `, `Weholite Attenuation`, `Conc Attenuation `,
 * `Box Culverts`, `Carriageway Take off`. `Mains Services`' key column also
 * moved from B to A in this revision (`RS_MS_001` is now `A7`, not `B7`).
 * The map now covers 17 sheets / 1160 keys total (was 11 sheets / 581 keys).
 *
 * Sheets still absent from this map because they carry no `RS_*` key at all
 * in the current template: `Notes`, `Trial Pit Log`, `Foul Summary`,
 * `Storm Summary`, `Carriageway Summary`, `Capping Layers`,
 * `Spoil Reconcilliation`, `SEWER RATES`, `MANHOLE RATES`.
 *
 * **Known template data-quality issues found while re-scanning (not fixed
 * here — informational only, since neither breaks this validator's exact
 * per-sheet `cellRef` lookup)**:
 *  - `Mains Services!AC37` (a "Total Quant" number cell, `0` in every other
 *    row) contains the stray string `"RS_MS_001"` instead of a number —
 *    almost certainly a copy-paste slip in the source sheet, not a second
 *    key. Excluded from this map (which only takes column A on that sheet).
 *  - The key prefix `RS_CT_*` is reused by two unrelated sheets — `Conc
 *    Attenuation ` (150 keys, `RS_CT_001`..`RS_CT_150`) and `Carriageway
 *    Take off` (40 keys, `RS_CT_001`..`RS_CT_040`). Harmless for validation
 *    (each is checked only within its own sheet), but the two are **not**
 *    interchangeable — don't assume a bare `RS_CT_NNN` identifies a unique
 *    row/column without also knowing which sheet it came from.
 *
 * Two column layouts are both in use here, unlike Groundworks' single
 * row-1-key-per-column layout:
 *  - **Column-per-field** ("Foul Manhole Schedule", "Foul Take off",
 *    "Storm Manhole Schedule", "Storm Take off", "Carriageway Take off"):
 *    key lives in row 1, one per column, same convention as
 *    `groundworksTakeoffColumnMap.ts`.
 *  - **Row-per-item** ("Prelims", "Pump Station", "Rising Main",
 *    "Ponds & Swales", "Cellular Attenuation ", "Weholite Attenuation",
 *    "Conc Attenuation ", "Box Culverts", "Mains Services",
 *    "Substation Base", "POS", "Compound"): these list items down the rows
 *    rather than across columns (per direct feedback: "for preliminaryItems
 *    keys needs to be placed vertically as items are listed row wise" — the
 *    same shape turned out to apply to every sheet in this group, not just
 *    Prelims), so the key lives once per row instead, in a single column
 *    (column A for every row-per-item sheet in this revision, including
 *    `Mains Services` now that its key moved from B to A).
 *
 * `cellRef` (shared with `TakeoffColumnDefinition` from
 * `groundworksTakeoffColumnMap.ts` — same shape, so both maps' entries are
 * interchangeable for a shared validator) is the exact cell address of that
 * key in the reference template, read directly off the template the same
 * way as Groundworks: an exact-string-match scan of every cell, not assumed
 * from row/column position. Every row-per-item sheet has gaps between key
 * rows (section headers, blank spacer rows between items), and the
 * column-per-field sheets skip columns too (e.g. "Foul Take off" has no key
 * at R1 — …Q1, then S1 onward), so a naive sequential derivation is wrong
 * for every sheet here. `label` is not yet captured for any sheet in this
 * map (unlike Groundworks' "PLOT C&F QUANTS").
 *
 * Regenerate by re-reading the template whenever it changes.
 */
export const ROADS_AND_SEWERS_TAKEOFF_COLUMN_MAP: Record<string, TakeoffColumnDefinition[]> = {
  "Prelims": [
    { key: "RS_P_0001", cellRef: "A8" },
    { key: "RS_P_0002", cellRef: "A9" },
    { key: "RS_P_0003", cellRef: "A10" },
    { key: "RS_P_0004", cellRef: "A12" },
    { key: "RS_P_0005", cellRef: "A13" },
    { key: "RS_P_0006", cellRef: "A14" },
    { key: "RS_P_0007", cellRef: "A16" },
    { key: "RS_P_0008", cellRef: "A17" },
    { key: "RS_P_0009", cellRef: "A18" },
    { key: "RS_P_0010", cellRef: "A19" },
    { key: "RS_P_0011", cellRef: "A21" },
    { key: "RS_P_0012", cellRef: "A23" },
    { key: "RS_P_0013", cellRef: "A24" },
    { key: "RS_P_0014", cellRef: "A25" },
    { key: "RS_P_0015", cellRef: "A26" },
    { key: "RS_P_0016", cellRef: "A28" },
    { key: "RS_P_0017", cellRef: "A29" },
    { key: "RS_P_0018", cellRef: "A31" },
    { key: "RS_P_0019", cellRef: "A32" },
    { key: "RS_P_0020", cellRef: "A34" },
    { key: "RS_P_0021", cellRef: "A35" },
    { key: "RS_P_0022", cellRef: "A37" },
    { key: "RS_P_0023", cellRef: "A38" },
    { key: "RS_P_0024", cellRef: "A40" },
    { key: "RS_P_0025", cellRef: "A41" },
    { key: "RS_P_0026", cellRef: "A43" },
    { key: "RS_P_0027", cellRef: "A46" },
    { key: "RS_P_0028", cellRef: "A47" },
    { key: "RS_P_0029", cellRef: "A50" },
    { key: "RS_P_0030", cellRef: "A51" },
    { key: "RS_P_0031", cellRef: "A54" },
    { key: "RS_P_0032", cellRef: "A55" },
    { key: "RS_P_0033", cellRef: "A56" },
    { key: "RS_P_0034", cellRef: "A58" },
    { key: "RS_P_0035", cellRef: "A60" },
    { key: "RS_P_0036", cellRef: "A62" },
    { key: "RS_P_0037", cellRef: "A63" },
    { key: "RS_P_0038", cellRef: "A66" },
    { key: "RS_P_0039", cellRef: "A67" },
    { key: "RS_P_0040", cellRef: "A68" },
    { key: "RS_P_0041", cellRef: "A69" },
    { key: "RS_P_0042", cellRef: "A70" },
    { key: "RS_P_0043", cellRef: "A71" },
    { key: "RS_P_0044", cellRef: "A72" },
    { key: "RS_P_0045", cellRef: "A75" },
    { key: "RS_P_0046", cellRef: "A76" },
    { key: "RS_P_0047", cellRef: "A82" },
    { key: "RS_P_0048", cellRef: "A83" },
    { key: "RS_P_0049", cellRef: "A84" },
    { key: "RS_P_0050", cellRef: "A89" },
    { key: "RS_P_0051", cellRef: "A90" },
    { key: "RS_P_0052", cellRef: "A91" },
    { key: "RS_P_0053", cellRef: "A97" },
    { key: "RS_P_0054", cellRef: "A98" },
    { key: "RS_P_0055", cellRef: "A99" },
    { key: "RS_P_0056", cellRef: "A103" },
    { key: "RS_P_0057", cellRef: "A104" },
    { key: "RS_P_0058", cellRef: "A105" },
    { key: "RS_P_0059", cellRef: "A109" },
    { key: "RS_P_0060", cellRef: "A110" },
    { key: "RS_P_0061", cellRef: "A111" },
    { key: "RS_P_0062", cellRef: "A112" },
    { key: "RS_P_0063", cellRef: "A113" },
    { key: "RS_P_0064", cellRef: "A114" },
  ],
  "Foul Manhole Schedule": [
    { key: "RS_FS_001", cellRef: "B1" },
    { key: "RS_FS_002", cellRef: "C1" },
    { key: "RS_FS_003", cellRef: "D1" },
    { key: "RS_FS_004", cellRef: "E1" },
    { key: "RS_FS_005", cellRef: "F1" },
    { key: "RS_FS_006", cellRef: "G1" },
    { key: "RS_FS_007", cellRef: "H1" },
    { key: "RS_FS_008", cellRef: "I1" },
    { key: "RS_FS_009", cellRef: "J1" },
    { key: "RS_FS_010", cellRef: "K1" },
    { key: "RS_FS_011", cellRef: "L1" },
    { key: "RS_FS_012", cellRef: "M1" },
    { key: "RS_FS_013", cellRef: "N1" },
    { key: "RS_FS_014", cellRef: "P1" },
  ],
  "Foul Take off": [
    { key: "RS_FT_001", cellRef: "B1" },
    { key: "RS_FT_002", cellRef: "C1" },
    { key: "RS_FT_003", cellRef: "D1" },
    { key: "RS_FT_004", cellRef: "E1" },
    { key: "RS_FT_005", cellRef: "F1" },
    { key: "RS_FT_006", cellRef: "G1" },
    { key: "RS_FT_007", cellRef: "H1" },
    { key: "RS_FT_008", cellRef: "I1" },
    { key: "RS_FT_009", cellRef: "J1" },
    { key: "RS_FT_010", cellRef: "K1" },
    { key: "RS_FT_011", cellRef: "L1" },
    { key: "RS_FT_012", cellRef: "M1" },
    { key: "RS_FT_013", cellRef: "N1" },
    { key: "RS_FT_014", cellRef: "O1" },
    { key: "RS_FT_015", cellRef: "P1" },
    { key: "RS_FT_016", cellRef: "Q1" },
    { key: "RS_FT_017", cellRef: "S1" },
    { key: "RS_FT_018", cellRef: "T1" },
    { key: "RS_FT_019", cellRef: "U1" },
    { key: "RS_FT_020", cellRef: "V1" },
    { key: "RS_FT_021", cellRef: "W1" },
    { key: "RS_FT_022", cellRef: "X1" },
    { key: "RS_FT_023", cellRef: "Y1" },
    { key: "RS_FT_024", cellRef: "Z1" },
    { key: "RS_FT_025", cellRef: "AA1" },
    { key: "RS_FT_026", cellRef: "AB1" },
    { key: "RS_FT_027", cellRef: "AC1" },
    { key: "RS_FT_028", cellRef: "AD1" },
    { key: "RS_FT_029", cellRef: "AE1" },
  ],
  "Pump Station": [
    { key: "RS_PS_001", cellRef: "A22" },
    { key: "RS_PS_002", cellRef: "A23" },
    { key: "RS_PS_003", cellRef: "A24" },
    { key: "RS_PS_004", cellRef: "A26" },
    { key: "RS_PS_005", cellRef: "A27" },
    { key: "RS_PS_006", cellRef: "A28" },
    { key: "RS_PS_007", cellRef: "A29" },
    { key: "RS_PS_008", cellRef: "A30" },
    { key: "RS_PS_009", cellRef: "A31" },
    { key: "RS_PS_010", cellRef: "A32" },
    { key: "RS_PS_011", cellRef: "A33" },
    { key: "RS_PS_012", cellRef: "A34" },
    { key: "RS_PS_013", cellRef: "A35" },
    { key: "RS_PS_014", cellRef: "A36" },
    { key: "RS_PS_015", cellRef: "A37" },
    { key: "RS_PS_016", cellRef: "A38" },
    { key: "RS_PS_017", cellRef: "A50" },
    { key: "RS_PS_018", cellRef: "A51" },
    { key: "RS_PS_019", cellRef: "A52" },
    { key: "RS_PS_020", cellRef: "A53" },
    { key: "RS_PS_021", cellRef: "A54" },
    { key: "RS_PS_022", cellRef: "A55" },
    { key: "RS_PS_023", cellRef: "A56" },
    { key: "RS_PS_024", cellRef: "A57" },
    { key: "RS_PS_025", cellRef: "A58" },
    { key: "RS_PS_026", cellRef: "A59" },
    { key: "RS_PS_027", cellRef: "A60" },
    { key: "RS_PS_028", cellRef: "A61" },
    { key: "RS_PS_029", cellRef: "A63" },
    { key: "RS_PS_030", cellRef: "A78" },
    { key: "RS_PS_031", cellRef: "A79" },
    { key: "RS_PS_032", cellRef: "A80" },
    { key: "RS_PS_033", cellRef: "A81" },
    { key: "RS_PS_034", cellRef: "A82" },
    { key: "RS_PS_035", cellRef: "A83" },
    { key: "RS_PS_036", cellRef: "A84" },
    { key: "RS_PS_037", cellRef: "A85" },
    { key: "RS_PS_038", cellRef: "A86" },
    { key: "RS_PS_039", cellRef: "A87" },
    { key: "RS_PS_040", cellRef: "A88" },
    { key: "RS_PS_041", cellRef: "A89" },
    { key: "RS_PS_042", cellRef: "A90" },
    { key: "RS_PS_043", cellRef: "A105" },
    { key: "RS_PS_044", cellRef: "A106" },
    { key: "RS_PS_045", cellRef: "A107" },
    { key: "RS_PS_046", cellRef: "A108" },
    { key: "RS_PS_047", cellRef: "A109" },
    { key: "RS_PS_048", cellRef: "A110" },
    { key: "RS_PS_049", cellRef: "A111" },
    { key: "RS_PS_050", cellRef: "A112" },
    { key: "RS_PS_051", cellRef: "A113" },
    { key: "RS_PS_052", cellRef: "A114" },
    { key: "RS_PS_053", cellRef: "A115" },
    { key: "RS_PS_054", cellRef: "A116" },
    { key: "RS_PS_055", cellRef: "A117" },
    { key: "RS_PS_056", cellRef: "A120" },
    { key: "RS_PS_057", cellRef: "A121" },
    { key: "RS_PS_058", cellRef: "A122" },
    { key: "RS_PS_059", cellRef: "A123" },
    { key: "RS_PS_060", cellRef: "A124" },
    { key: "RS_PS_061", cellRef: "A125" },
    { key: "RS_PS_062", cellRef: "A126" },
    { key: "RS_PS_063", cellRef: "A132" },
    { key: "RS_PS_064", cellRef: "A140" },
    { key: "RS_PS_065", cellRef: "A141" },
    { key: "RS_PS_066", cellRef: "A142" },
    { key: "RS_PS_067", cellRef: "A144" },
    { key: "RS_PS_068", cellRef: "A145" },
    { key: "RS_PS_069", cellRef: "A146" },
    { key: "RS_PS_070", cellRef: "A147" },
    { key: "RS_PS_071", cellRef: "A148" },
    { key: "RS_PS_072", cellRef: "A150" },
    { key: "RS_PS_073", cellRef: "A151" },
    { key: "RS_PS_074", cellRef: "A154" },
    { key: "RS_PS_075", cellRef: "A155" },
    { key: "RS_PS_076", cellRef: "A157" },
    { key: "RS_PS_077", cellRef: "A158" },
    { key: "RS_PS_078", cellRef: "A159" },
    { key: "RS_PS_079", cellRef: "A160" },
    { key: "RS_PS_080", cellRef: "A161" },
    { key: "RS_PS_081", cellRef: "A162" },
    { key: "RS_PS_082", cellRef: "A164" },
    { key: "RS_PS_083", cellRef: "A165" },
    { key: "RS_PS_084", cellRef: "A167" },
    { key: "RS_PS_085", cellRef: "A168" },
    { key: "RS_PS_086", cellRef: "A169" },
    { key: "RS_PS_087", cellRef: "A170" },
  ],
  "Rising Main": [
    { key: "RS_RM_001", cellRef: "A6" },
    { key: "RS_RM_002", cellRef: "A7" },
    { key: "RS_RM_003", cellRef: "A8" },
    { key: "RS_RM_004", cellRef: "A9" },
    { key: "RS_RM_005", cellRef: "A11" },
    { key: "RS_RM_006", cellRef: "A12" },
    { key: "RS_RM_007", cellRef: "A13" },
    { key: "RS_RM_008", cellRef: "A14" },
    { key: "RS_RM_009", cellRef: "A16" },
    { key: "RS_RM_010", cellRef: "A17" },
    { key: "RS_RM_011", cellRef: "A18" },
    { key: "RS_RM_012", cellRef: "A19" },
    { key: "RS_RM_013", cellRef: "A20" },
    { key: "RS_RM_014", cellRef: "A21" },
    { key: "RS_RM_015", cellRef: "A22" },
    { key: "RS_RM_016", cellRef: "A23" },
    { key: "RS_RM_017", cellRef: "A24" },
    { key: "RS_RM_018", cellRef: "A25" },
    { key: "RS_RM_019", cellRef: "A26" },
    { key: "RS_RM_020", cellRef: "A28" },
    { key: "RS_RM_021", cellRef: "A29" },
    { key: "RS_RM_022", cellRef: "A30" },
    { key: "RS_RM_023", cellRef: "A31" },
    { key: "RS_RM_024", cellRef: "A33" },
    { key: "RS_RM_025", cellRef: "A34" },
    { key: "RS_RM_026", cellRef: "A35" },
    { key: "RS_RM_027", cellRef: "A36" },
    { key: "RS_RM_028", cellRef: "A38" },
    { key: "RS_RM_029", cellRef: "A39" },
    { key: "RS_RM_030", cellRef: "A40" },
    { key: "RS_RM_031", cellRef: "A41" },
    { key: "RS_RM_032", cellRef: "A43" },
    { key: "RS_RM_033", cellRef: "A44" },
    { key: "RS_RM_034", cellRef: "A45" },
    { key: "RS_RM_035", cellRef: "A46" },
    { key: "RS_RM_036", cellRef: "A47" },
    { key: "RS_RM_037", cellRef: "A48" },
    { key: "RS_RM_038", cellRef: "A50" },
    { key: "RS_RM_039", cellRef: "A51" },
    { key: "RS_RM_040", cellRef: "A52" },
    { key: "RS_RM_041", cellRef: "A53" },
    { key: "RS_RM_042", cellRef: "A55" },
    { key: "RS_RM_043", cellRef: "A57" },
    { key: "RS_RM_044", cellRef: "A59" },
    { key: "RS_RM_045", cellRef: "A60" },
    { key: "RS_RM_046", cellRef: "A62" },
  ],
  "Storm Manhole Schedule": [
    { key: "RS_SS_001", cellRef: "B1" },
    { key: "RS_SS_002", cellRef: "C1" },
    { key: "RS_SS_003", cellRef: "D1" },
    { key: "RS_SS_004", cellRef: "E1" },
    { key: "RS_SS_005", cellRef: "F1" },
    { key: "RS_SS_006", cellRef: "G1" },
    { key: "RS_SS_007", cellRef: "H1" },
    { key: "RS_SS_008", cellRef: "I1" },
    { key: "RS_SS_009", cellRef: "J1" },
    { key: "RS_SS_010", cellRef: "K1" },
    { key: "RS_SS_011", cellRef: "L1" },
    { key: "RS_SS_012", cellRef: "M1" },
    { key: "RS_SS_013", cellRef: "N1" },
    { key: "RS_SS_014", cellRef: "O1" },
    { key: "RS_SS_015", cellRef: "Q1" },
  ],
  "Storm Take off": [
    { key: "RS_ST_001", cellRef: "B1" },
    { key: "RS_ST_002", cellRef: "C1" },
    { key: "RS_ST_003", cellRef: "D1" },
    { key: "RS_ST_004", cellRef: "E1" },
    { key: "RS_ST_005", cellRef: "F1" },
    { key: "RS_ST_006", cellRef: "G1" },
    { key: "RS_ST_007", cellRef: "H1" },
    { key: "RS_ST_008", cellRef: "I1" },
    { key: "RS_ST_009", cellRef: "J1" },
    { key: "RS_ST_010", cellRef: "K1" },
    { key: "RS_ST_011", cellRef: "L1" },
    { key: "RS_ST_012", cellRef: "M1" },
    { key: "RS_ST_013", cellRef: "N1" },
    { key: "RS_ST_014", cellRef: "O1" },
    { key: "RS_ST_015", cellRef: "P1" },
    { key: "RS_ST_016", cellRef: "Q1" },
    { key: "RS_ST_017", cellRef: "S1" },
    { key: "RS_ST_018", cellRef: "T1" },
    { key: "RS_ST_019", cellRef: "U1" },
    { key: "RS_ST_020", cellRef: "V1" },
    { key: "RS_ST_021", cellRef: "W1" },
    { key: "RS_ST_022", cellRef: "X1" },
    { key: "RS_ST_023", cellRef: "Y1" },
    { key: "RS_ST_024", cellRef: "Z1" },
    { key: "RS_ST_025", cellRef: "AA1" },
    { key: "RS_ST_026", cellRef: "AB1" },
    { key: "RS_ST_027", cellRef: "AC1" },
    { key: "RS_ST_028", cellRef: "AD1" },
    { key: "RS_ST_029", cellRef: "AE1" },
    { key: "RS_ST_030", cellRef: "AG1" },
    { key: "RS_ST_031", cellRef: "AH1" },
    { key: "RS_ST_032", cellRef: "AI1" },
    { key: "RS_ST_033", cellRef: "AJ1" },
    { key: "RS_ST_034", cellRef: "AK1" },
    { key: "RS_ST_035", cellRef: "AL1" },
    { key: "RS_ST_036", cellRef: "AM1" },
    { key: "RS_ST_037", cellRef: "AN1" },
    { key: "RS_ST_038", cellRef: "AO1" },
    { key: "RS_ST_039", cellRef: "AP1" },
    { key: "RS_ST_040", cellRef: "AQ1" },
    { key: "RS_ST_041", cellRef: "AR1" },
  ],
  "Ponds & Swales": [
    { key: "RS_PW_001", cellRef: "A8" },
    { key: "RS_PW_002", cellRef: "A9" },
    { key: "RS_PW_003", cellRef: "A11" },
    { key: "RS_PW_004", cellRef: "A12" },
    { key: "RS_PW_005", cellRef: "A13" },
    { key: "RS_PW_006", cellRef: "A14" },
    { key: "RS_PW_007", cellRef: "A15" },
    { key: "RS_PW_008", cellRef: "A16" },
    { key: "RS_PW_009", cellRef: "A17" },
    { key: "RS_PW_010", cellRef: "A18" },
    { key: "RS_PW_011", cellRef: "A22" },
    { key: "RS_PW_012", cellRef: "A23" },
    { key: "RS_PW_013", cellRef: "A25" },
    { key: "RS_PW_014", cellRef: "A26" },
    { key: "RS_PW_015", cellRef: "A27" },
    { key: "RS_PW_016", cellRef: "A28" },
    { key: "RS_PW_017", cellRef: "A29" },
    { key: "RS_PW_018", cellRef: "A30" },
    { key: "RS_PW_019", cellRef: "A31" },
    { key: "RS_PW_020", cellRef: "A32" },
    { key: "RS_PW_021", cellRef: "A35" },
    { key: "RS_PW_022", cellRef: "A36" },
    { key: "RS_PW_023", cellRef: "A38" },
    { key: "RS_PW_024", cellRef: "A39" },
    { key: "RS_PW_025", cellRef: "A40" },
    { key: "RS_PW_026", cellRef: "A41" },
    { key: "RS_PW_027", cellRef: "A42" },
    { key: "RS_PW_028", cellRef: "A43" },
    { key: "RS_PW_029", cellRef: "A44" },
    { key: "RS_PW_030", cellRef: "A45" },
    { key: "RS_PW_031", cellRef: "A48" },
    { key: "RS_PW_032", cellRef: "A49" },
    { key: "RS_PW_033", cellRef: "A51" },
    { key: "RS_PW_034", cellRef: "A52" },
    { key: "RS_PW_035", cellRef: "A53" },
    { key: "RS_PW_036", cellRef: "A54" },
    { key: "RS_PW_037", cellRef: "A55" },
    { key: "RS_PW_038", cellRef: "A56" },
    { key: "RS_PW_039", cellRef: "A57" },
    { key: "RS_PW_040", cellRef: "A58" },
    { key: "RS_PW_041", cellRef: "A61" },
    { key: "RS_PW_042", cellRef: "A62" },
    { key: "RS_PW_043", cellRef: "A64" },
    { key: "RS_PW_044", cellRef: "A65" },
    { key: "RS_PW_045", cellRef: "A66" },
    { key: "RS_PW_046", cellRef: "A67" },
    { key: "RS_PW_047", cellRef: "A68" },
    { key: "RS_PW_048", cellRef: "A69" },
    { key: "RS_PW_049", cellRef: "A70" },
    { key: "RS_PW_050", cellRef: "A71" },
    { key: "RS_PW_051", cellRef: "A74" },
    { key: "RS_PW_052", cellRef: "A75" },
    { key: "RS_PW_053", cellRef: "A77" },
    { key: "RS_PW_054", cellRef: "A78" },
    { key: "RS_PW_055", cellRef: "A79" },
    { key: "RS_PW_056", cellRef: "A80" },
    { key: "RS_PW_057", cellRef: "A81" },
    { key: "RS_PW_058", cellRef: "A82" },
    { key: "RS_PW_059", cellRef: "A83" },
    { key: "RS_PW_060", cellRef: "A84" },
    { key: "RS_PW_061", cellRef: "A87" },
    { key: "RS_PW_062", cellRef: "A88" },
    { key: "RS_PW_063", cellRef: "A90" },
    { key: "RS_PW_064", cellRef: "A91" },
    { key: "RS_PW_065", cellRef: "A92" },
    { key: "RS_PW_066", cellRef: "A93" },
    { key: "RS_PW_067", cellRef: "A94" },
    { key: "RS_PW_068", cellRef: "A95" },
    { key: "RS_PW_069", cellRef: "A96" },
    { key: "RS_PW_070", cellRef: "A97" },
    { key: "RS_PW_071", cellRef: "A100" },
    { key: "RS_PW_072", cellRef: "A101" },
    { key: "RS_PW_073", cellRef: "A103" },
    { key: "RS_PW_074", cellRef: "A104" },
    { key: "RS_PW_075", cellRef: "A105" },
    { key: "RS_PW_076", cellRef: "A106" },
    { key: "RS_PW_077", cellRef: "A107" },
    { key: "RS_PW_078", cellRef: "A108" },
    { key: "RS_PW_079", cellRef: "A109" },
    { key: "RS_PW_080", cellRef: "A110" },
    { key: "RS_PW_081", cellRef: "A113" },
    { key: "RS_PW_082", cellRef: "A114" },
    { key: "RS_PW_083", cellRef: "A116" },
    { key: "RS_PW_084", cellRef: "A117" },
    { key: "RS_PW_085", cellRef: "A118" },
    { key: "RS_PW_086", cellRef: "A119" },
    { key: "RS_PW_087", cellRef: "A120" },
    { key: "RS_PW_088", cellRef: "A121" },
    { key: "RS_PW_089", cellRef: "A122" },
    { key: "RS_PW_090", cellRef: "A123" },
    { key: "RS_PW_091", cellRef: "A126" },
    { key: "RS_PW_092", cellRef: "A127" },
    { key: "RS_PW_093", cellRef: "A129" },
    { key: "RS_PW_094", cellRef: "A130" },
    { key: "RS_PW_095", cellRef: "A131" },
    { key: "RS_PW_096", cellRef: "A132" },
    { key: "RS_PW_097", cellRef: "A133" },
    { key: "RS_PW_098", cellRef: "A134" },
    { key: "RS_PW_099", cellRef: "A135" },
    { key: "RS_PW_100", cellRef: "A136" },
    { key: "RS_PW_101", cellRef: "A140" },
    { key: "RS_PW_102", cellRef: "A141" },
    { key: "RS_PW_103", cellRef: "A142" },
    { key: "RS_PW_104", cellRef: "A143" },
    { key: "RS_PW_105", cellRef: "A144" },
    { key: "RS_PW_106", cellRef: "A145" },
    { key: "RS_PW_107", cellRef: "A146" },
    { key: "RS_PW_108", cellRef: "A147" },
    { key: "RS_PW_109", cellRef: "A148" },
    { key: "RS_PW_110", cellRef: "A149" },
    { key: "RS_PW_111", cellRef: "A151" },
    { key: "RS_PW_112", cellRef: "A153" },
    { key: "RS_PW_113", cellRef: "A155" },
    { key: "RS_PW_114", cellRef: "A158" },
    { key: "RS_PW_115", cellRef: "A159" },
    { key: "RS_PW_116", cellRef: "A161" },
    { key: "RS_PW_117", cellRef: "A162" },
    { key: "RS_PW_118", cellRef: "A163" },
    { key: "RS_PW_119", cellRef: "A164" },
    { key: "RS_PW_120", cellRef: "A165" },
    { key: "RS_PW_121", cellRef: "A166" },
    { key: "RS_PW_122", cellRef: "A169" },
    { key: "RS_PW_123", cellRef: "A170" },
    { key: "RS_PW_124", cellRef: "A172" },
    { key: "RS_PW_125", cellRef: "A173" },
    { key: "RS_PW_126", cellRef: "A174" },
    { key: "RS_PW_127", cellRef: "A175" },
    { key: "RS_PW_128", cellRef: "A176" },
    { key: "RS_PW_129", cellRef: "A177" },
    { key: "RS_PW_130", cellRef: "A180" },
    { key: "RS_PW_131", cellRef: "A181" },
    { key: "RS_PW_132", cellRef: "A183" },
    { key: "RS_PW_133", cellRef: "A184" },
    { key: "RS_PW_134", cellRef: "A185" },
    { key: "RS_PW_135", cellRef: "A186" },
    { key: "RS_PW_136", cellRef: "A187" },
    { key: "RS_PW_137", cellRef: "A188" },
    { key: "RS_PW_138", cellRef: "A191" },
    { key: "RS_PW_139", cellRef: "A192" },
    { key: "RS_PW_140", cellRef: "A194" },
    { key: "RS_PW_141", cellRef: "A195" },
    { key: "RS_PW_142", cellRef: "A196" },
    { key: "RS_PW_143", cellRef: "A197" },
    { key: "RS_PW_144", cellRef: "A198" },
    { key: "RS_PW_145", cellRef: "A199" },
    { key: "RS_PW_146", cellRef: "A202" },
    { key: "RS_PW_147", cellRef: "A203" },
    { key: "RS_PW_148", cellRef: "A205" },
    { key: "RS_PW_149", cellRef: "A206" },
    { key: "RS_PW_150", cellRef: "A207" },
    { key: "RS_PW_151", cellRef: "A208" },
    { key: "RS_PW_152", cellRef: "A209" },
    { key: "RS_PW_153", cellRef: "A210" },
    { key: "RS_PW_154", cellRef: "A213" },
    { key: "RS_PW_155", cellRef: "A214" },
    { key: "RS_PW_156", cellRef: "A216" },
    { key: "RS_PW_157", cellRef: "A217" },
    { key: "RS_PW_158", cellRef: "A218" },
    { key: "RS_PW_159", cellRef: "A219" },
    { key: "RS_PW_160", cellRef: "A220" },
    { key: "RS_PW_161", cellRef: "A221" },
    { key: "RS_PW_162", cellRef: "A224" },
    { key: "RS_PW_163", cellRef: "A225" },
    { key: "RS_PW_164", cellRef: "A227" },
    { key: "RS_PW_165", cellRef: "A228" },
    { key: "RS_PW_166", cellRef: "A229" },
    { key: "RS_PW_167", cellRef: "A230" },
    { key: "RS_PW_168", cellRef: "A231" },
    { key: "RS_PW_169", cellRef: "A232" },
    { key: "RS_PW_170", cellRef: "A235" },
    { key: "RS_PW_171", cellRef: "A236" },
    { key: "RS_PW_172", cellRef: "A238" },
    { key: "RS_PW_173", cellRef: "A239" },
    { key: "RS_PW_174", cellRef: "A240" },
    { key: "RS_PW_175", cellRef: "A241" },
    { key: "RS_PW_176", cellRef: "A242" },
    { key: "RS_PW_177", cellRef: "A243" },
    { key: "RS_PW_178", cellRef: "A246" },
    { key: "RS_PW_179", cellRef: "A247" },
    { key: "RS_PW_180", cellRef: "A249" },
    { key: "RS_PW_181", cellRef: "A250" },
    { key: "RS_PW_182", cellRef: "A251" },
    { key: "RS_PW_183", cellRef: "A252" },
    { key: "RS_PW_184", cellRef: "A253" },
    { key: "RS_PW_185", cellRef: "A254" },
    { key: "RS_PW_186", cellRef: "A257" },
    { key: "RS_PW_187", cellRef: "A258" },
    { key: "RS_PW_188", cellRef: "A260" },
    { key: "RS_PW_189", cellRef: "A261" },
    { key: "RS_PW_190", cellRef: "A262" },
    { key: "RS_PW_191", cellRef: "A263" },
    { key: "RS_PW_192", cellRef: "A264" },
    { key: "RS_PW_193", cellRef: "A265" },
  ],
  "Cellular Attenuation ": [
    { key: "RS_CA_001", cellRef: "A22" },
    { key: "RS_CA_002", cellRef: "A23" },
    { key: "RS_CA_003", cellRef: "A24" },
    { key: "RS_CA_004", cellRef: "A25" },
    { key: "RS_CA_005", cellRef: "A26" },
    { key: "RS_CA_006", cellRef: "A27" },
    { key: "RS_CA_007", cellRef: "A28" },
    { key: "RS_CA_008", cellRef: "A29" },
    { key: "RS_CA_009", cellRef: "A30" },
    { key: "RS_CA_010", cellRef: "A31" },
    { key: "RS_CA_011", cellRef: "A32" },
    { key: "RS_CA_012", cellRef: "A57" },
    { key: "RS_CA_013", cellRef: "A58" },
    { key: "RS_CA_014", cellRef: "A59" },
    { key: "RS_CA_015", cellRef: "A60" },
    { key: "RS_CA_016", cellRef: "A61" },
    { key: "RS_CA_017", cellRef: "A62" },
    { key: "RS_CA_018", cellRef: "A63" },
    { key: "RS_CA_019", cellRef: "A64" },
    { key: "RS_CA_020", cellRef: "A65" },
    { key: "RS_CA_021", cellRef: "A66" },
    { key: "RS_CA_022", cellRef: "A67" },
    { key: "RS_CA_023", cellRef: "A92" },
    { key: "RS_CA_024", cellRef: "A93" },
    { key: "RS_CA_025", cellRef: "A94" },
    { key: "RS_CA_026", cellRef: "A95" },
    { key: "RS_CA_027", cellRef: "A96" },
    { key: "RS_CA_028", cellRef: "A97" },
    { key: "RS_CA_029", cellRef: "A98" },
    { key: "RS_CA_030", cellRef: "A99" },
    { key: "RS_CA_031", cellRef: "A100" },
    { key: "RS_CA_032", cellRef: "A101" },
    { key: "RS_CA_033", cellRef: "A102" },
    { key: "RS_CA_034", cellRef: "A127" },
    { key: "RS_CA_035", cellRef: "A128" },
    { key: "RS_CA_036", cellRef: "A129" },
    { key: "RS_CA_037", cellRef: "A130" },
    { key: "RS_CA_038", cellRef: "A131" },
    { key: "RS_CA_039", cellRef: "A132" },
    { key: "RS_CA_040", cellRef: "A133" },
    { key: "RS_CA_041", cellRef: "A134" },
    { key: "RS_CA_042", cellRef: "A135" },
    { key: "RS_CA_043", cellRef: "A136" },
    { key: "RS_CA_044", cellRef: "A137" },
    { key: "RS_CA_045", cellRef: "A162" },
    { key: "RS_CA_046", cellRef: "A163" },
    { key: "RS_CA_047", cellRef: "A164" },
    { key: "RS_CA_048", cellRef: "A165" },
    { key: "RS_CA_049", cellRef: "A166" },
    { key: "RS_CA_050", cellRef: "A167" },
    { key: "RS_CA_051", cellRef: "A168" },
    { key: "RS_CA_052", cellRef: "A169" },
    { key: "RS_CA_053", cellRef: "A170" },
    { key: "RS_CA_054", cellRef: "A171" },
    { key: "RS_CA_055", cellRef: "A172" },
    { key: "RS_CA_056", cellRef: "A197" },
    { key: "RS_CA_057", cellRef: "A198" },
    { key: "RS_CA_058", cellRef: "A199" },
    { key: "RS_CA_059", cellRef: "A200" },
    { key: "RS_CA_060", cellRef: "A201" },
    { key: "RS_CA_061", cellRef: "A202" },
    { key: "RS_CA_062", cellRef: "A203" },
    { key: "RS_CA_063", cellRef: "A204" },
    { key: "RS_CA_064", cellRef: "A205" },
    { key: "RS_CA_065", cellRef: "A206" },
    { key: "RS_CA_066", cellRef: "A207" },
    { key: "RS_CA_067", cellRef: "A232" },
    { key: "RS_CA_068", cellRef: "A233" },
    { key: "RS_CA_069", cellRef: "A234" },
    { key: "RS_CA_070", cellRef: "A235" },
    { key: "RS_CA_071", cellRef: "A236" },
    { key: "RS_CA_072", cellRef: "A237" },
    { key: "RS_CA_073", cellRef: "A238" },
    { key: "RS_CA_074", cellRef: "A239" },
    { key: "RS_CA_075", cellRef: "A240" },
    { key: "RS_CA_076", cellRef: "A241" },
    { key: "RS_CA_077", cellRef: "A242" },
    { key: "RS_CA_078", cellRef: "A267" },
    { key: "RS_CA_079", cellRef: "A268" },
    { key: "RS_CA_080", cellRef: "A269" },
    { key: "RS_CA_081", cellRef: "A270" },
    { key: "RS_CA_082", cellRef: "A271" },
    { key: "RS_CA_083", cellRef: "A272" },
    { key: "RS_CA_084", cellRef: "A273" },
    { key: "RS_CA_085", cellRef: "A274" },
    { key: "RS_CA_086", cellRef: "A275" },
    { key: "RS_CA_087", cellRef: "A276" },
    { key: "RS_CA_088", cellRef: "A277" },
    { key: "RS_CA_089", cellRef: "A302" },
    { key: "RS_CA_090", cellRef: "A303" },
    { key: "RS_CA_091", cellRef: "A304" },
    { key: "RS_CA_092", cellRef: "A305" },
    { key: "RS_CA_093", cellRef: "A306" },
    { key: "RS_CA_094", cellRef: "A307" },
    { key: "RS_CA_095", cellRef: "A308" },
    { key: "RS_CA_096", cellRef: "A309" },
    { key: "RS_CA_097", cellRef: "A310" },
    { key: "RS_CA_098", cellRef: "A311" },
    { key: "RS_CA_099", cellRef: "A312" },
    { key: "RS_CA_100", cellRef: "A337" },
    { key: "RS_CA_101", cellRef: "A338" },
    { key: "RS_CA_102", cellRef: "A339" },
    { key: "RS_CA_103", cellRef: "A340" },
    { key: "RS_CA_104", cellRef: "A341" },
    { key: "RS_CA_105", cellRef: "A342" },
    { key: "RS_CA_106", cellRef: "A343" },
    { key: "RS_CA_107", cellRef: "A344" },
    { key: "RS_CA_108", cellRef: "A345" },
    { key: "RS_CA_109", cellRef: "A346" },
    { key: "RS_CA_110", cellRef: "A347" },
  ],
  "Weholite Attenuation": [
    { key: "RS_WA_001", cellRef: "A12" },
    { key: "RS_WA_002", cellRef: "A13" },
    { key: "RS_WA_003", cellRef: "A14" },
    { key: "RS_WA_004", cellRef: "A16" },
    { key: "RS_WA_005", cellRef: "A17" },
    { key: "RS_WA_006", cellRef: "A18" },
    { key: "RS_WA_007", cellRef: "A19" },
    { key: "RS_WA_008", cellRef: "A20" },
    { key: "RS_WA_009", cellRef: "A22" },
    { key: "RS_WA_010", cellRef: "A23" },
    { key: "RS_WA_011", cellRef: "A26" },
    { key: "RS_WA_012", cellRef: "A27" },
    { key: "RS_WA_013", cellRef: "A29" },
    { key: "RS_WA_014", cellRef: "A30" },
    { key: "RS_WA_015", cellRef: "A31" },
    { key: "RS_WA_016", cellRef: "A32" },
    { key: "RS_WA_017", cellRef: "A33" },
    { key: "RS_WA_018", cellRef: "A34" },
    { key: "RS_WA_019", cellRef: "A36" },
    { key: "RS_WA_020", cellRef: "A37" },
    { key: "RS_WA_021", cellRef: "A39" },
    { key: "RS_WA_022", cellRef: "A40" },
    { key: "RS_WA_023", cellRef: "A41" },
    { key: "RS_WA_024", cellRef: "A42" },
    { key: "RS_WA_025", cellRef: "A63" },
    { key: "RS_WA_026", cellRef: "A64" },
    { key: "RS_WA_027", cellRef: "A65" },
    { key: "RS_WA_028", cellRef: "A67" },
    { key: "RS_WA_029", cellRef: "A68" },
    { key: "RS_WA_030", cellRef: "A69" },
    { key: "RS_WA_031", cellRef: "A70" },
    { key: "RS_WA_032", cellRef: "A71" },
    { key: "RS_WA_033", cellRef: "A73" },
    { key: "RS_WA_034", cellRef: "A74" },
    { key: "RS_WA_035", cellRef: "A77" },
    { key: "RS_WA_036", cellRef: "A78" },
    { key: "RS_WA_037", cellRef: "A80" },
    { key: "RS_WA_038", cellRef: "A81" },
    { key: "RS_WA_039", cellRef: "A82" },
    { key: "RS_WA_040", cellRef: "A83" },
    { key: "RS_WA_041", cellRef: "A84" },
    { key: "RS_WA_042", cellRef: "A85" },
    { key: "RS_WA_043", cellRef: "A87" },
    { key: "RS_WA_044", cellRef: "A88" },
    { key: "RS_WA_045", cellRef: "A90" },
    { key: "RS_WA_046", cellRef: "A91" },
    { key: "RS_WA_047", cellRef: "A92" },
    { key: "RS_WA_048", cellRef: "A93" },
    { key: "RS_WA_049", cellRef: "A114" },
    { key: "RS_WA_050", cellRef: "A115" },
    { key: "RS_WA_051", cellRef: "A116" },
    { key: "RS_WA_052", cellRef: "A118" },
    { key: "RS_WA_053", cellRef: "A119" },
    { key: "RS_WA_054", cellRef: "A120" },
    { key: "RS_WA_055", cellRef: "A121" },
    { key: "RS_WA_056", cellRef: "A122" },
    { key: "RS_WA_057", cellRef: "A124" },
    { key: "RS_WA_058", cellRef: "A125" },
    { key: "RS_WA_059", cellRef: "A128" },
    { key: "RS_WA_060", cellRef: "A129" },
    { key: "RS_WA_061", cellRef: "A131" },
    { key: "RS_WA_062", cellRef: "A132" },
    { key: "RS_WA_063", cellRef: "A133" },
    { key: "RS_WA_064", cellRef: "A134" },
    { key: "RS_WA_065", cellRef: "A135" },
    { key: "RS_WA_066", cellRef: "A136" },
    { key: "RS_WA_067", cellRef: "A138" },
    { key: "RS_WA_068", cellRef: "A139" },
    { key: "RS_WA_069", cellRef: "A141" },
    { key: "RS_WA_070", cellRef: "A142" },
    { key: "RS_WA_071", cellRef: "A143" },
    { key: "RS_WA_072", cellRef: "A144" },
    { key: "RS_WA_073", cellRef: "A165" },
    { key: "RS_WA_074", cellRef: "A166" },
    { key: "RS_WA_075", cellRef: "A167" },
    { key: "RS_WA_076", cellRef: "A169" },
    { key: "RS_WA_077", cellRef: "A170" },
    { key: "RS_WA_078", cellRef: "A171" },
    { key: "RS_WA_079", cellRef: "A172" },
    { key: "RS_WA_080", cellRef: "A173" },
    { key: "RS_WA_081", cellRef: "A175" },
    { key: "RS_WA_082", cellRef: "A176" },
    { key: "RS_WA_083", cellRef: "A179" },
    { key: "RS_WA_084", cellRef: "A180" },
    { key: "RS_WA_085", cellRef: "A182" },
    { key: "RS_WA_086", cellRef: "A183" },
    { key: "RS_WA_087", cellRef: "A184" },
    { key: "RS_WA_088", cellRef: "A185" },
    { key: "RS_WA_089", cellRef: "A186" },
    { key: "RS_WA_090", cellRef: "A187" },
    { key: "RS_WA_091", cellRef: "A189" },
    { key: "RS_WA_092", cellRef: "A190" },
    { key: "RS_WA_093", cellRef: "A192" },
    { key: "RS_WA_094", cellRef: "A193" },
    { key: "RS_WA_095", cellRef: "A194" },
    { key: "RS_WA_096", cellRef: "A195" },
    { key: "RS_WA_097", cellRef: "A216" },
    { key: "RS_WA_098", cellRef: "A217" },
    { key: "RS_WA_099", cellRef: "A218" },
    { key: "RS_WA_100", cellRef: "A220" },
    { key: "RS_WA_101", cellRef: "A221" },
    { key: "RS_WA_102", cellRef: "A222" },
    { key: "RS_WA_103", cellRef: "A223" },
    { key: "RS_WA_104", cellRef: "A224" },
    { key: "RS_WA_105", cellRef: "A226" },
    { key: "RS_WA_106", cellRef: "A227" },
    { key: "RS_WA_107", cellRef: "A230" },
    { key: "RS_WA_108", cellRef: "A231" },
    { key: "RS_WA_109", cellRef: "A233" },
    { key: "RS_WA_110", cellRef: "A234" },
    { key: "RS_WA_111", cellRef: "A235" },
    { key: "RS_WA_112", cellRef: "A236" },
    { key: "RS_WA_113", cellRef: "A237" },
    { key: "RS_WA_114", cellRef: "A238" },
    { key: "RS_WA_115", cellRef: "A240" },
    { key: "RS_WA_116", cellRef: "A241" },
    { key: "RS_WA_117", cellRef: "A243" },
    { key: "RS_WA_118", cellRef: "A244" },
    { key: "RS_WA_119", cellRef: "A245" },
    { key: "RS_WA_120", cellRef: "A246" },
  ],
  "Conc Attenuation ": [
    { key: "RS_CT_001", cellRef: "A16" },
    { key: "RS_CT_002", cellRef: "A17" },
    { key: "RS_CT_003", cellRef: "A18" },
    { key: "RS_CT_004", cellRef: "A20" },
    { key: "RS_CT_005", cellRef: "A21" },
    { key: "RS_CT_006", cellRef: "A22" },
    { key: "RS_CT_007", cellRef: "A23" },
    { key: "RS_CT_008", cellRef: "A24" },
    { key: "RS_CT_009", cellRef: "A25" },
    { key: "RS_CT_010", cellRef: "A26" },
    { key: "RS_CT_011", cellRef: "A28" },
    { key: "RS_CT_012", cellRef: "A29" },
    { key: "RS_CT_013", cellRef: "A30" },
    { key: "RS_CT_014", cellRef: "A31" },
    { key: "RS_CT_015", cellRef: "A32" },
    { key: "RS_CT_016", cellRef: "A34" },
    { key: "RS_CT_017", cellRef: "A35" },
    { key: "RS_CT_018", cellRef: "A36" },
    { key: "RS_CT_019", cellRef: "A38" },
    { key: "RS_CT_020", cellRef: "A39" },
    { key: "RS_CT_021", cellRef: "A40" },
    { key: "RS_CT_022", cellRef: "A41" },
    { key: "RS_CT_023", cellRef: "A43" },
    { key: "RS_CT_024", cellRef: "A44" },
    { key: "RS_CT_025", cellRef: "A45" },
    { key: "RS_CT_026", cellRef: "A46" },
    { key: "RS_CT_027", cellRef: "A47" },
    { key: "RS_CT_028", cellRef: "A48" },
    { key: "RS_CT_029", cellRef: "A49" },
    { key: "RS_CT_030", cellRef: "A50" },
    { key: "RS_CT_031", cellRef: "A66" },
    { key: "RS_CT_032", cellRef: "A67" },
    { key: "RS_CT_033", cellRef: "A68" },
    { key: "RS_CT_034", cellRef: "A70" },
    { key: "RS_CT_035", cellRef: "A71" },
    { key: "RS_CT_036", cellRef: "A72" },
    { key: "RS_CT_037", cellRef: "A73" },
    { key: "RS_CT_038", cellRef: "A74" },
    { key: "RS_CT_039", cellRef: "A75" },
    { key: "RS_CT_040", cellRef: "A76" },
    { key: "RS_CT_041", cellRef: "A78" },
    { key: "RS_CT_042", cellRef: "A79" },
    { key: "RS_CT_043", cellRef: "A80" },
    { key: "RS_CT_044", cellRef: "A81" },
    { key: "RS_CT_045", cellRef: "A82" },
    { key: "RS_CT_046", cellRef: "A84" },
    { key: "RS_CT_047", cellRef: "A85" },
    { key: "RS_CT_048", cellRef: "A86" },
    { key: "RS_CT_049", cellRef: "A88" },
    { key: "RS_CT_050", cellRef: "A89" },
    { key: "RS_CT_051", cellRef: "A90" },
    { key: "RS_CT_052", cellRef: "A91" },
    { key: "RS_CT_053", cellRef: "A93" },
    { key: "RS_CT_054", cellRef: "A94" },
    { key: "RS_CT_055", cellRef: "A95" },
    { key: "RS_CT_056", cellRef: "A96" },
    { key: "RS_CT_057", cellRef: "A97" },
    { key: "RS_CT_058", cellRef: "A98" },
    { key: "RS_CT_059", cellRef: "A99" },
    { key: "RS_CT_060", cellRef: "A100" },
    { key: "RS_CT_061", cellRef: "A116" },
    { key: "RS_CT_062", cellRef: "A117" },
    { key: "RS_CT_063", cellRef: "A118" },
    { key: "RS_CT_064", cellRef: "A120" },
    { key: "RS_CT_065", cellRef: "A121" },
    { key: "RS_CT_066", cellRef: "A122" },
    { key: "RS_CT_067", cellRef: "A123" },
    { key: "RS_CT_068", cellRef: "A124" },
    { key: "RS_CT_069", cellRef: "A125" },
    { key: "RS_CT_070", cellRef: "A126" },
    { key: "RS_CT_071", cellRef: "A128" },
    { key: "RS_CT_072", cellRef: "A129" },
    { key: "RS_CT_073", cellRef: "A130" },
    { key: "RS_CT_074", cellRef: "A131" },
    { key: "RS_CT_075", cellRef: "A132" },
    { key: "RS_CT_076", cellRef: "A134" },
    { key: "RS_CT_077", cellRef: "A135" },
    { key: "RS_CT_078", cellRef: "A136" },
    { key: "RS_CT_079", cellRef: "A138" },
    { key: "RS_CT_080", cellRef: "A139" },
    { key: "RS_CT_081", cellRef: "A140" },
    { key: "RS_CT_082", cellRef: "A141" },
    { key: "RS_CT_083", cellRef: "A143" },
    { key: "RS_CT_084", cellRef: "A144" },
    { key: "RS_CT_085", cellRef: "A145" },
    { key: "RS_CT_086", cellRef: "A146" },
    { key: "RS_CT_087", cellRef: "A147" },
    { key: "RS_CT_088", cellRef: "A148" },
    { key: "RS_CT_089", cellRef: "A149" },
    { key: "RS_CT_090", cellRef: "A150" },
    { key: "RS_CT_091", cellRef: "A166" },
    { key: "RS_CT_092", cellRef: "A167" },
    { key: "RS_CT_093", cellRef: "A168" },
    { key: "RS_CT_094", cellRef: "A170" },
    { key: "RS_CT_095", cellRef: "A171" },
    { key: "RS_CT_096", cellRef: "A172" },
    { key: "RS_CT_097", cellRef: "A173" },
    { key: "RS_CT_098", cellRef: "A174" },
    { key: "RS_CT_099", cellRef: "A175" },
    { key: "RS_CT_100", cellRef: "A176" },
    { key: "RS_CT_101", cellRef: "A178" },
    { key: "RS_CT_102", cellRef: "A179" },
    { key: "RS_CT_103", cellRef: "A180" },
    { key: "RS_CT_104", cellRef: "A181" },
    { key: "RS_CT_105", cellRef: "A182" },
    { key: "RS_CT_106", cellRef: "A184" },
    { key: "RS_CT_107", cellRef: "A185" },
    { key: "RS_CT_108", cellRef: "A186" },
    { key: "RS_CT_109", cellRef: "A188" },
    { key: "RS_CT_110", cellRef: "A189" },
    { key: "RS_CT_111", cellRef: "A190" },
    { key: "RS_CT_112", cellRef: "A191" },
    { key: "RS_CT_113", cellRef: "A193" },
    { key: "RS_CT_114", cellRef: "A194" },
    { key: "RS_CT_115", cellRef: "A195" },
    { key: "RS_CT_116", cellRef: "A196" },
    { key: "RS_CT_117", cellRef: "A197" },
    { key: "RS_CT_118", cellRef: "A198" },
    { key: "RS_CT_119", cellRef: "A199" },
    { key: "RS_CT_120", cellRef: "A200" },
    { key: "RS_CT_121", cellRef: "A216" },
    { key: "RS_CT_122", cellRef: "A217" },
    { key: "RS_CT_123", cellRef: "A218" },
    { key: "RS_CT_124", cellRef: "A220" },
    { key: "RS_CT_125", cellRef: "A221" },
    { key: "RS_CT_126", cellRef: "A222" },
    { key: "RS_CT_127", cellRef: "A223" },
    { key: "RS_CT_128", cellRef: "A224" },
    { key: "RS_CT_129", cellRef: "A225" },
    { key: "RS_CT_130", cellRef: "A226" },
    { key: "RS_CT_131", cellRef: "A228" },
    { key: "RS_CT_132", cellRef: "A229" },
    { key: "RS_CT_133", cellRef: "A230" },
    { key: "RS_CT_134", cellRef: "A231" },
    { key: "RS_CT_135", cellRef: "A232" },
    { key: "RS_CT_136", cellRef: "A234" },
    { key: "RS_CT_137", cellRef: "A235" },
    { key: "RS_CT_138", cellRef: "A236" },
    { key: "RS_CT_139", cellRef: "A238" },
    { key: "RS_CT_140", cellRef: "A239" },
    { key: "RS_CT_141", cellRef: "A240" },
    { key: "RS_CT_142", cellRef: "A241" },
    { key: "RS_CT_143", cellRef: "A243" },
    { key: "RS_CT_144", cellRef: "A244" },
    { key: "RS_CT_145", cellRef: "A245" },
    { key: "RS_CT_146", cellRef: "A246" },
    { key: "RS_CT_147", cellRef: "A247" },
    { key: "RS_CT_148", cellRef: "A248" },
    { key: "RS_CT_149", cellRef: "A249" },
    { key: "RS_CT_150", cellRef: "A250" },
  ],
  "Box Culverts": [
    { key: "RS_BC_001", cellRef: "A14" },
    { key: "RS_BC_002", cellRef: "A15" },
    { key: "RS_BC_003", cellRef: "A16" },
    { key: "RS_BC_004", cellRef: "A17" },
    { key: "RS_BC_005", cellRef: "A18" },
    { key: "RS_BC_006", cellRef: "A19" },
    { key: "RS_BC_007", cellRef: "A20" },
    { key: "RS_BC_008", cellRef: "A21" },
    { key: "RS_BC_009", cellRef: "A22" },
    { key: "RS_BC_010", cellRef: "A23" },
    { key: "RS_BC_011", cellRef: "A37" },
    { key: "RS_BC_012", cellRef: "A38" },
    { key: "RS_BC_013", cellRef: "A39" },
    { key: "RS_BC_014", cellRef: "A40" },
    { key: "RS_BC_015", cellRef: "A41" },
    { key: "RS_BC_016", cellRef: "A42" },
    { key: "RS_BC_017", cellRef: "A43" },
    { key: "RS_BC_018", cellRef: "A44" },
    { key: "RS_BC_019", cellRef: "A45" },
    { key: "RS_BC_020", cellRef: "A46" },
    { key: "RS_BC_021", cellRef: "A59" },
    { key: "RS_BC_022", cellRef: "A60" },
    { key: "RS_BC_023", cellRef: "A61" },
    { key: "RS_BC_024", cellRef: "A62" },
    { key: "RS_BC_025", cellRef: "A63" },
    { key: "RS_BC_026", cellRef: "A64" },
    { key: "RS_BC_027", cellRef: "A65" },
    { key: "RS_BC_028", cellRef: "A66" },
    { key: "RS_BC_029", cellRef: "A67" },
    { key: "RS_BC_030", cellRef: "A68" },
    { key: "RS_BC_031", cellRef: "A82" },
    { key: "RS_BC_032", cellRef: "A83" },
    { key: "RS_BC_033", cellRef: "A84" },
    { key: "RS_BC_034", cellRef: "A85" },
    { key: "RS_BC_035", cellRef: "A86" },
    { key: "RS_BC_036", cellRef: "A87" },
    { key: "RS_BC_037", cellRef: "A88" },
    { key: "RS_BC_038", cellRef: "A89" },
    { key: "RS_BC_039", cellRef: "A90" },
    { key: "RS_BC_040", cellRef: "A91" },
    { key: "RS_BC_041", cellRef: "A105" },
    { key: "RS_BC_042", cellRef: "A106" },
    { key: "RS_BC_043", cellRef: "A107" },
    { key: "RS_BC_044", cellRef: "A108" },
    { key: "RS_BC_045", cellRef: "A109" },
    { key: "RS_BC_046", cellRef: "A110" },
    { key: "RS_BC_047", cellRef: "A111" },
    { key: "RS_BC_048", cellRef: "A112" },
    { key: "RS_BC_049", cellRef: "A113" },
    { key: "RS_BC_050", cellRef: "A114" },
  ],
  "Carriageway Take off": [
    { key: "RS_CT_001", cellRef: "C1" },
    { key: "RS_CT_002", cellRef: "D1" },
    { key: "RS_CT_003", cellRef: "E1" },
    { key: "RS_CT_004", cellRef: "F1" },
    { key: "RS_CT_005", cellRef: "G1" },
    { key: "RS_CT_006", cellRef: "H1" },
    { key: "RS_CT_007", cellRef: "I1" },
    { key: "RS_CT_008", cellRef: "J1" },
    { key: "RS_CT_009", cellRef: "L1" },
    { key: "RS_CT_010", cellRef: "M1" },
    { key: "RS_CT_011", cellRef: "N1" },
    { key: "RS_CT_012", cellRef: "O1" },
    { key: "RS_CT_013", cellRef: "P1" },
    { key: "RS_CT_014", cellRef: "Q1" },
    { key: "RS_CT_015", cellRef: "R1" },
    { key: "RS_CT_016", cellRef: "S1" },
    { key: "RS_CT_017", cellRef: "T1" },
    { key: "RS_CT_018", cellRef: "U1" },
    { key: "RS_CT_019", cellRef: "V1" },
    { key: "RS_CT_020", cellRef: "W1" },
    { key: "RS_CT_021", cellRef: "X1" },
    { key: "RS_CT_022", cellRef: "Y1" },
    { key: "RS_CT_023", cellRef: "Z1" },
    { key: "RS_CT_024", cellRef: "AA1" },
    { key: "RS_CT_025", cellRef: "AB1" },
    { key: "RS_CT_026", cellRef: "AC1" },
    { key: "RS_CT_027", cellRef: "AD1" },
    { key: "RS_CT_028", cellRef: "AE1" },
    { key: "RS_CT_029", cellRef: "AF1" },
    { key: "RS_CT_030", cellRef: "AG1" },
    { key: "RS_CT_031", cellRef: "AI1" },
    { key: "RS_CT_032", cellRef: "AJ1" },
    { key: "RS_CT_033", cellRef: "AK1" },
    { key: "RS_CT_034", cellRef: "AL1" },
    { key: "RS_CT_035", cellRef: "AM1" },
    { key: "RS_CT_036", cellRef: "AN1" },
    { key: "RS_CT_037", cellRef: "AP1" },
    { key: "RS_CT_038", cellRef: "AQ1" },
    { key: "RS_CT_039", cellRef: "AR1" },
    { key: "RS_CT_040", cellRef: "AS1" },
  ],
  "Mains Services": [
    { key: "RS_MS_001", cellRef: "A7" },
    { key: "RS_MS_002", cellRef: "A9" },
    { key: "RS_MS_003", cellRef: "A10" },
    { key: "RS_MS_004", cellRef: "A12" },
    { key: "RS_MS_005", cellRef: "A15" },
    { key: "RS_MS_006", cellRef: "A17" },
    { key: "RS_MS_007", cellRef: "A18" },
    { key: "RS_MS_008", cellRef: "A19" },
    { key: "RS_MS_009", cellRef: "A20" },
    { key: "RS_MS_010", cellRef: "A21" },
    { key: "RS_MS_011", cellRef: "A22" },
    { key: "RS_MS_012", cellRef: "A23" },
    { key: "RS_MS_013", cellRef: "A24" },
    { key: "RS_MS_014", cellRef: "A26" },
    { key: "RS_MS_015", cellRef: "A27" },
    { key: "RS_MS_016", cellRef: "A28" },
    { key: "RS_MS_017", cellRef: "A29" },
    { key: "RS_MS_018", cellRef: "A31" },
    { key: "RS_MS_019", cellRef: "A32" },
    { key: "RS_MS_020", cellRef: "A33" },
    { key: "RS_MS_021", cellRef: "A34" },
    { key: "RS_MS_022", cellRef: "A35" },
    { key: "RS_MS_023", cellRef: "A36" },
    { key: "RS_MS_024", cellRef: "A37" },
    { key: "RS_MS_025", cellRef: "A39" },
    { key: "RS_MS_026", cellRef: "A40" },
    { key: "RS_MS_027", cellRef: "A41" },
    { key: "RS_MS_028", cellRef: "A43" },
    { key: "RS_MS_029", cellRef: "A45" },
    { key: "RS_MS_030", cellRef: "A46" },
    { key: "RS_MS_031", cellRef: "A47" },
    { key: "RS_MS_032", cellRef: "A48" },
    { key: "RS_MS_033", cellRef: "A49" },
    { key: "RS_MS_034", cellRef: "A50" },
    { key: "RS_MS_035", cellRef: "A52" },
    { key: "RS_MS_036", cellRef: "A53" },
    { key: "RS_MS_037", cellRef: "A55" },
  ],
  "Substation Base": [
    { key: "RS_SB_001", cellRef: "A6" },
    { key: "RS_SB_002", cellRef: "A7" },
    { key: "RS_SB_003", cellRef: "A8" },
    { key: "RS_SB_004", cellRef: "A9" },
    { key: "RS_SB_005", cellRef: "A10" },
    { key: "RS_SB_006", cellRef: "A11" },
    { key: "RS_SB_007", cellRef: "A12" },
    { key: "RS_SB_008", cellRef: "A13" },
    { key: "RS_SB_009", cellRef: "A14" },
    { key: "RS_SB_010", cellRef: "A15" },
    { key: "RS_SB_011", cellRef: "A16" },
    { key: "RS_SB_012", cellRef: "A17" },
    { key: "RS_SB_013", cellRef: "A18" },
    { key: "RS_SB_014", cellRef: "A19" },
    { key: "RS_SB_015", cellRef: "A20" },
    { key: "RS_SB_016", cellRef: "A21" },
    { key: "RS_SB_017", cellRef: "A22" },
    { key: "RS_SB_018", cellRef: "A23" },
    { key: "RS_SB_019", cellRef: "A24" },
    { key: "RS_SB_020", cellRef: "A27" },
    { key: "RS_SB_021", cellRef: "A28" },
    { key: "RS_SB_022", cellRef: "A29" },
    { key: "RS_SB_023", cellRef: "A30" },
    { key: "RS_SB_024", cellRef: "A31" },
    { key: "RS_SB_025", cellRef: "A32" },
    { key: "RS_SB_026", cellRef: "A33" },
  ],
  "POS": [
    { key: "RS_POS_001", cellRef: "A7" },
    { key: "RS_POS_002", cellRef: "A8" },
    { key: "RS_POS_003", cellRef: "A9" },
    { key: "RS_POS_004", cellRef: "A10" },
    { key: "RS_POS_005", cellRef: "A11" },
    { key: "RS_POS_006", cellRef: "A12" },
    { key: "RS_POS_007", cellRef: "A16" },
    { key: "RS_POS_008", cellRef: "A17" },
    { key: "RS_POS_009", cellRef: "A18" },
    { key: "RS_POS_010", cellRef: "A20" },
    { key: "RS_POS_011", cellRef: "A24" },
    { key: "RS_POS_012", cellRef: "A25" },
    { key: "RS_POS_013", cellRef: "A26" },
    { key: "RS_POS_014", cellRef: "A27" },
    { key: "RS_POS_015", cellRef: "A28" },
    { key: "RS_POS_016", cellRef: "A30" },
    { key: "RS_POS_017", cellRef: "A38" },
    { key: "RS_POS_018", cellRef: "A39" },
    { key: "RS_POS_019", cellRef: "A40" },
    { key: "RS_POS_020", cellRef: "A41" },
    { key: "RS_POS_021", cellRef: "A42" },
    { key: "RS_POS_022", cellRef: "A43" },
    { key: "RS_POS_023", cellRef: "A47" },
    { key: "RS_POS_024", cellRef: "A48" },
    { key: "RS_POS_025", cellRef: "A49" },
    { key: "RS_POS_026", cellRef: "A51" },
    { key: "RS_POS_027", cellRef: "A55" },
    { key: "RS_POS_028", cellRef: "A56" },
    { key: "RS_POS_029", cellRef: "A57" },
    { key: "RS_POS_030", cellRef: "A58" },
    { key: "RS_POS_031", cellRef: "A59" },
    { key: "RS_POS_032", cellRef: "A61" },
    { key: "RS_POS_033", cellRef: "A69" },
    { key: "RS_POS_034", cellRef: "A70" },
    { key: "RS_POS_035", cellRef: "A71" },
    { key: "RS_POS_036", cellRef: "A72" },
    { key: "RS_POS_037", cellRef: "A73" },
    { key: "RS_POS_038", cellRef: "A74" },
    { key: "RS_POS_039", cellRef: "A78" },
    { key: "RS_POS_040", cellRef: "A79" },
    { key: "RS_POS_041", cellRef: "A80" },
    { key: "RS_POS_042", cellRef: "A82" },
    { key: "RS_POS_043", cellRef: "A86" },
    { key: "RS_POS_044", cellRef: "A87" },
    { key: "RS_POS_045", cellRef: "A88" },
    { key: "RS_POS_046", cellRef: "A89" },
    { key: "RS_POS_047", cellRef: "A90" },
    { key: "RS_POS_048", cellRef: "A92" },
    { key: "RS_POS_049", cellRef: "A100" },
    { key: "RS_POS_050", cellRef: "A101" },
    { key: "RS_POS_051", cellRef: "A102" },
    { key: "RS_POS_052", cellRef: "A103" },
    { key: "RS_POS_053", cellRef: "A104" },
    { key: "RS_POS_054", cellRef: "A105" },
    { key: "RS_POS_055", cellRef: "A109" },
    { key: "RS_POS_056", cellRef: "A110" },
    { key: "RS_POS_057", cellRef: "A111" },
    { key: "RS_POS_058", cellRef: "A113" },
    { key: "RS_POS_059", cellRef: "A117" },
    { key: "RS_POS_060", cellRef: "A118" },
    { key: "RS_POS_061", cellRef: "A119" },
    { key: "RS_POS_062", cellRef: "A120" },
    { key: "RS_POS_063", cellRef: "A121" },
    { key: "RS_POS_064", cellRef: "A123" },
    { key: "RS_POS_065", cellRef: "A131" },
    { key: "RS_POS_066", cellRef: "A132" },
    { key: "RS_POS_067", cellRef: "A133" },
    { key: "RS_POS_068", cellRef: "A134" },
    { key: "RS_POS_069", cellRef: "A135" },
    { key: "RS_POS_070", cellRef: "A136" },
    { key: "RS_POS_071", cellRef: "A140" },
    { key: "RS_POS_072", cellRef: "A141" },
    { key: "RS_POS_073", cellRef: "A142" },
    { key: "RS_POS_074", cellRef: "A144" },
    { key: "RS_POS_075", cellRef: "A148" },
    { key: "RS_POS_076", cellRef: "A149" },
    { key: "RS_POS_077", cellRef: "A150" },
    { key: "RS_POS_078", cellRef: "A151" },
    { key: "RS_POS_079", cellRef: "A152" },
    { key: "RS_POS_080", cellRef: "A154" },
  ],
  "Compound": [
    { key: "RS_CP_001", cellRef: "A8" },
    { key: "RS_CP_002", cellRef: "A9" },
    { key: "RS_CP_003", cellRef: "A10" },
    { key: "RS_CP_004", cellRef: "A11" },
    { key: "RS_CP_005", cellRef: "A12" },
    { key: "RS_CP_006", cellRef: "A13" },
    { key: "RS_CP_007", cellRef: "A14" },
    { key: "RS_CP_008", cellRef: "A15" },
    { key: "RS_CP_009", cellRef: "A16" },
    { key: "RS_CP_010", cellRef: "A17" },
    { key: "RS_CP_011", cellRef: "A18" },
    { key: "RS_CP_012", cellRef: "A19" },
    { key: "RS_CP_013", cellRef: "A20" },
    { key: "RS_CP_014", cellRef: "A21" },
    { key: "RS_CP_015", cellRef: "A22" },
    { key: "RS_CP_016", cellRef: "A25" },
    { key: "RS_CP_017", cellRef: "A26" },
    { key: "RS_CP_018", cellRef: "A27" },
    { key: "RS_CP_019", cellRef: "A28" },
    { key: "RS_CP_020", cellRef: "A29" },
    { key: "RS_CP_021", cellRef: "A30" },
    { key: "RS_CP_022", cellRef: "A31" },
    { key: "RS_CP_023", cellRef: "A32" },
    { key: "RS_CP_024", cellRef: "A33" },
    { key: "RS_CP_025", cellRef: "A36" },
    { key: "RS_CP_026", cellRef: "A37" },
    { key: "RS_CP_027", cellRef: "A38" },
    { key: "RS_CP_028", cellRef: "A39" },
    { key: "RS_CP_029", cellRef: "A40" },
    { key: "RS_CP_030", cellRef: "A41" },
    { key: "RS_CP_031", cellRef: "A44" },
    { key: "RS_CP_032", cellRef: "A45" },
    { key: "RS_CP_033", cellRef: "A46" },
    { key: "RS_CP_034", cellRef: "A47" },
    { key: "RS_CP_035", cellRef: "A48" },
    { key: "RS_CP_036", cellRef: "A49" },
    { key: "RS_CP_037", cellRef: "A52" },
    { key: "RS_CP_038", cellRef: "A53" },
    { key: "RS_CP_039", cellRef: "A54" },
    { key: "RS_CP_040", cellRef: "A55" },
    { key: "RS_CP_041", cellRef: "A60" },
    { key: "RS_CP_042", cellRef: "A61" },
    { key: "RS_CP_043", cellRef: "A62" },
    { key: "RS_CP_044", cellRef: "A63" },
    { key: "RS_CP_045", cellRef: "A65" },
    { key: "RS_CP_046", cellRef: "A66" },
    { key: "RS_CP_047", cellRef: "A67" },
    { key: "RS_CP_048", cellRef: "A68" },
    { key: "RS_CP_049", cellRef: "A69" },
    { key: "RS_CP_050", cellRef: "A70" },
    { key: "RS_CP_051", cellRef: "A71" },
    { key: "RS_CP_052", cellRef: "A72" },
    { key: "RS_CP_053", cellRef: "A78" },
    { key: "RS_CP_054", cellRef: "A79" },
    { key: "RS_CP_055", cellRef: "A80" },
    { key: "RS_CP_056", cellRef: "A84" },
    { key: "RS_CP_057", cellRef: "A85" },
    { key: "RS_CP_058", cellRef: "A86" },
  ],
};
